import { useState, useEffect } from 'react';
import { X, Rocket, Brain, FileText, Search } from 'lucide-react';
import { labApi } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { CONTENT_SECTIONS } from './constants';

interface GenerateAllModalProps {
  productId: string;
  hasCompleteKb: boolean;
  onClose: () => void;
  onStarted: () => void;
}

const STYLE_PRESETS = [
  { id: 'social_proof', label: 'Social Proof' },
  { id: 'problem_solution', label: 'Problem → Rozwiązanie' },
  { id: 'lifestyle', label: 'Lifestyle' },
  { id: 'fear_urgency', label: 'Fear / Urgency' },
  { id: 'testimonial', label: 'Testimonial' },
];

export function GenerateAllModal({ productId, hasCompleteKb, onClose, onStarted }: GenerateAllModalProps) {
  const [runResearch, setRunResearch] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['AD_COPY', 'LANDING', 'ADVERTORIAL']);
  const [variantsPerType, setVariantsPerType] = useState(1);
  const [selectedPresets, setSelectedPresets] = useState<string[]>([]);
  const [videoStrategies, setVideoStrategies] = useState<Array<{ key: string; label: string }>>([]);
  const [selectedStrategies, setSelectedStrategies] = useState<string[]>(['EXPLAINER']);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const videoSelected = selectedTypes.includes('VIDEO_SCRIPT');

  useEffect(() => {
    if (!videoSelected || videoStrategies.length > 0) return;
    labApi.getVideoStrategies()
      .then(res => setVideoStrategies(res.data.data?.strategies || []))
      .catch(err => console.error('Failed to load video strategies:', err));
  }, [videoSelected, videoStrategies.length]);

  const toggleType = (type: string) => {
    setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  const togglePreset = (id: string) => {
    setSelectedPresets(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const toggleStrategy = (key: string) => {
    setSelectedStrategies(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleStart = async () => {
    if (videoSelected && selectedStrategies.length === 0) return;
    setSubmitting(true);
    try {
      await labApi.generateAll(productId, {
        runResearch,
        contentTypes: selectedTypes,
        variantsPerType,
        stylePresets: selectedPresets,
        videoStrategies: videoSelected ? selectedStrategies : undefined,
      });
      onStarted();
    } catch (err) {
      console.error('Generate all failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-surface-raised rounded-2xl border border-border w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <Rocket className="w-5 h-5 text-accent-primary" />
            Tryb YOLO
          </h2>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Pipeline phases */}
          <div className="bg-surface-hover/50 rounded-lg p-4 border border-border-subtle space-y-3">
            <p className="text-xs font-medium text-text-secondary">Pipeline zostanie wykonany w kolejności:</p>

            {/* Research */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={runResearch}
                onChange={e => setRunResearch(e.target.checked)}
                className="rounded border-border-strong bg-surface-hover text-accent-primary focus:ring-accent-primary/20"
              />
              <Search className="w-4 h-4 text-accent-info" />
              <div>
                <span className="text-xs text-text-primary">1. Deep Research</span>
                <span className="text-[10px] text-text-muted ml-2">(wkrótce)</span>
              </div>
            </label>

            {/* KB */}
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full flex items-center justify-center ${hasCompleteKb ? 'bg-success/20' : 'bg-warning/20'}`}>
                <span className="text-[10px]">{hasCompleteKb ? '✓' : '2'}</span>
              </div>
              <Brain className="w-4 h-4 text-accent-primary" />
              <span className="text-xs text-text-primary">
                2. Baza wiedzy {hasCompleteKb ? <span className="text-success">(gotowa - pominięta)</span> : <span className="text-warning">(zostanie wygenerowana)</span>}
              </span>
            </div>

            {/* Content */}
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full flex items-center justify-center bg-info/20">
                <span className="text-[10px] text-accent-info">3</span>
              </div>
              <FileText className="w-4 h-4 text-accent-info" />
              <span className="text-xs text-text-primary">3. Generowanie treści (batch)</span>
            </div>
          </div>

          {/* Content types */}
          <div>
            <label className="text-xs font-medium text-text-secondary mb-2 block">Typy treści do wygenerowania</label>
            <div className="grid grid-cols-2 gap-1.5">
              {CONTENT_SECTIONS.map(section => {
                const Icon = section.icon;
                const isSelected = selectedTypes.includes(section.type);
                return (
                  <button
                    key={section.type}
                    onClick={() => toggleType(section.type)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] border transition-all ${
                      isSelected
                        ? `${section.bgClass} ${section.colorClass} ${section.borderClass}`
                        : 'text-text-muted border-border hover:border-border-strong'
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Strategie wideo (jeden skrypt per strategia) */}
          {videoSelected && (
            <div>
              <label className="text-xs font-medium text-text-secondary mb-2 block">Strategie reklam wideo</label>
              <div className="flex flex-wrap gap-1.5">
                {videoStrategies.map(strategy => {
                  const isSelected = selectedStrategies.includes(strategy.key);
                  return (
                    <button
                      key={strategy.key}
                      onClick={() => toggleStrategy(strategy.key)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] border transition-all ${
                        isSelected
                          ? 'bg-info/10 text-accent-info border-info/30'
                          : 'text-text-muted border-border hover:border-border-strong'
                      }`}
                    >
                      {strategy.label}
                    </button>
                  );
                })}
              </div>
              {selectedStrategies.length === 0 && (
                <p className="text-[10px] text-warning mt-1.5">Wybierz co najmniej jedną strategię dla reklam wideo.</p>
              )}
            </div>
          )}

          {/* Variants */}
          <div>
            <label className="text-xs font-medium text-text-secondary mb-2 block">
              Warianty per typ: <span className="text-text-primary">{variantsPerType}</span>
            </label>
            <input
              type="range"
              min={1}
              max={3}
              value={variantsPerType}
              onChange={e => setVariantsPerType(Number(e.target.value))}
              className="w-full accent-accent-primary"
            />
          </div>

          {/* Style presets */}
          <div>
            <label className="text-xs font-medium text-text-secondary mb-2 block">Style presets</label>
            <div className="flex flex-wrap gap-1.5">
              {STYLE_PRESETS.map(preset => {
                const isSelected = selectedPresets.includes(preset.id);
                return (
                  <button
                    key={preset.id}
                    onClick={() => togglePreset(preset.id)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] border transition-all ${
                      isSelected
                        ? 'bg-accent-primary/10 text-accent-primary border-accent-primary/30'
                        : 'text-text-muted border-border hover:border-border-strong'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-border">
          <Button
            variant="primary"
            size="md"
            onClick={handleStart}
            loading={submitting}
            disabled={submitting || (videoSelected && selectedStrategies.length === 0)}
            leftIcon={<Rocket />}
          >
            Uruchom generowanie
          </Button>
          <Button variant="ghost" size="md" onClick={onClose}>
            Anuluj
          </Button>
        </div>
      </div>
    </div>
  );
}
